<template>
  <div>
    <a-spin :spinning="loading">
      <div class="row mb-3">
        <div class="col">
          <a-button icon="plus" @click="openModal(null)" class="w-100">
            {{ $t("product.addProduct") }}
          </a-button>
        </div>
        <div class="col">
          <a-input
            :placeholder="$t('product.search')"
            @input="onSearch"
            class="w-100"
          />
        </div>
        <div class="col">
          <a-input-number
            :placeholder="$t('product.minprice')"
            v-model="minPrice"
            style="width: 100%"
            :min="0"
            @change="onPriceFilterChange"
          />
        </div>
        <div class="col">
          <a-input-number
            :placeholder="$t('product.maxprice')"
            v-model="maxPrice"
            style="width: 100%"
            :min="0"
            @change="onPriceFilterChange"
          />
        </div>
        <div class="col">
          <a-select
            v-model="selectedTags"
            :placeholder="$t('product.tags')"
            style="width: 100%"
            @change="onTagsFilterChange"
            mode="multiple"
          >
            <a-select-option
              v-for="tag in uniqueTags"
              :key="tag"
              :value="tag"
              >{{ tag }}</a-select-option
            >
          </a-select>
        </div>

        <div class="col-auto d-flex align-items-center justify-content-end">
          <a-switch
            default-checked
            :checked-children="$t('utilisateur.active')"
            :un-checked-children="$t('utilisateur.nonActive')"
            :checked="!showArchived"
            @change="toggleArchived"
          />
        </div>
      </div>

      <a-table
        :loading="tableLoading"
        :rowKey="'_id'"
        :columns="columns"
        :data-source="filteredData"
        :pagination="true"
        :scroll="{ x: 'max-content' }"
        @row-click="(record, event) => onRowClick(record, event)"
      >
        <!-- Custom columns -->
        <template
          v-for="col in ['availability', 'price', 'quantity', 'updatedAt']"
          :slot="col"
          slot-scope="text, record"
        >
          <div
            v-if="col === 'availability'"
            :key="'availability-' + record._id"
          >
            <a-tag :color="getTagColor(record.availability)">
              {{ getTagText(record.availability) }}
            </a-tag>
          </div>
          <div v-else-if="col === 'price'" :key="'price-' + record._id">
            {{ text }} {{ $t("product.currency") }}
          </div>
          <div v-else-if="col === 'quantity'" :key="'quantity-' + record._id">
            {{ text }}
          </div>
          <div v-else-if="col === 'updatedAt'" :key="'updatedAt-' + record._id">
            {{ moment(text).format("DD/MM/YYYY HH:mm") }}
          </div>
        </template>
        <template slot="productImage" slot-scope="text, record">
          <img
            v-if="record.coverPicture"
            :src="
              settings.resourcesUrl.replace('FILE_PATH', record.coverPicture)
            "
            style="width: 60px; height: 60px; border-radius: 5px"
          />
        </template>
        <template slot="tags" slot-scope="text, record">
          <div class="tag-container">
            <template v-if="record.tags && record.tags.length > 0">
              <!-- Display first 3 tags -->
              <a-tag
                color="blue"
                v-for="(tag, index) in record.tags.slice(0, 3)"
                :key="tag._id || index"
              >
                {{ tag.name }}
              </a-tag>

              <!-- Display +N more indicator with tooltip if there are more than 3 tags -->
              <a-tooltip v-if="record.tags.length > 3" placement="top">
                <template slot="title">
                  <div
                    v-for="tag in record.tags.slice(3)"
                    :key="tag._id || tag.name"
                  >
                    {{ tag.name }}
                  </div>
                </template>
                <a-tag class="more-tag" color="blue"
                  >+{{ record.tags.length - 3 }}</a-tag
                >
              </a-tooltip>
            </template>
            <template v-else>
              <span>-</span>
            </template>
          </div>
        </template>

        <span slot="operation" slot-scope="text, record">
          <span>
            <a-button
              class="mr-3"
              @click="() => $router.push('/product/' + record._id)"
            >
              <a-icon type="eye" />{{ $t("action.voir") }}
            </a-button>
          </span>

          <span v-if="!showArchived">
            <a-button class="mr-3" type="primary" @click="openModal(record)">
              <a-icon type="edit" />{{ $t("action.modifier") }}
            </a-button>
          </span>

          <span v-if="!showArchived">
            <a-popconfirm
              :title="$t('requis.supp')"
              @confirm="supp(record._id)"
            >
              <a-button type="danger">
                <a-icon type="delete" />
                <span style="margin-left: 8px">{{ $t("action.annuler") }}</span>
              </a-button>
            </a-popconfirm>
          </span>

          <span v-if="showArchived">
            <a-popconfirm
              :title="$t('all.sureTo')"
              @confirm="reactivateProduct(record._id)"
            >
              <a-button type="primary">
                <a-icon type="plus" />
                <span style="margin-left: 8px">{{ $t("liste.reactive") }}</span>
              </a-button>
            </a-popconfirm>
          </span>
        </span>

        <div
          slot="filterDropdown"
          slot-scope="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('personnel.chercher')}  ${column.title}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
            "
            @pressEnter="
              () => handleSearch(selectedKeys, confirm, column.dataIndex)
            "
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            style="width: 90px; margin-right: 8px"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >{{ $t("paiement.chercher") }}</a-button
          >
          <a-button
            size="small"
            style="width: 90px"
            @click="() => handleReset(clearFilters)"
            >{{ $t("action.reinitialiser") }}</a-button
          >
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <template slot="customRender" slot-scope="text, record, index, column">
          <span v-if="searchText && searchedColumn === column.dataIndex">
            <template
              v-for="(fragment, i) in text
                .toString()
                .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
            >
              <mark
                v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                :key="i"
                class="highlight"
                >{{ fragment }}
              </mark>
              <template v-else>{{ fragment }}</template>
            </template>
          </span>
        </template>
      </a-table>

      <ProductForm
        :visible="showModal"
        :mode="mode"
        :productData="currentProduct"
        :existingTags="uniqueTags"
        @close="handleCancel"
        @submit="handleProductSubmit"
        @productUpdated="handleProductUpdated"
      />
    </a-spin>
  </div>
</template>

<script>
import ProductForm from "./ProductForm.vue";
import moment from "moment";
import apiClient from "@/services/axios";
import { mapState } from "vuex";

export default {
  name: "ProductList",
  components: { ProductForm },
  data() {
    return {
      mode: "add",
      showModal: false,
      searchTerm: "",
      minPrice: null,
      maxPrice: null,
      selectedTags: [],
      currentProduct: null,
      data: [],
      loading: false,
      tableLoading: false,
      showArchived: false,
    };
  },
  computed: {
    ...mapState(["settings"]),
    uniqueTags() {
      const tagsSet = new Set();
      this.data.forEach((product) => {
        if (product.tags) {
          product.tags.forEach((tag) => {
            if (tag.name && typeof tag.name === "string") {
              tagsSet.add(tag.name);
            }
          });
        }
      });
      return [...tagsSet];
    },
    filteredData() {
      let filtered = this.data;

      filtered = filtered.filter((product) =>
        this.showArchived
          ? product.status === "inactive"
          : product.status === "active"
      );

      if (this.searchTerm) {
        filtered = filtered.filter(
          (item) =>
            (item.name &&
              item.name
                .toLowerCase()
                .includes(this.searchTerm.toLowerCase())) ||
            (item.quantity !== undefined &&
              item.quantity.toString().includes(this.searchTerm))
        );
      }

      if (this.searchText && this.searchedColumn) {
        filtered = filtered.filter((item) => {
          const value = item[this.searchedColumn];
          return (
            value &&
            value
              .toString()
              .toLowerCase()
              .includes(this.searchText.toLowerCase())
          );
        });
      }

      if (this.minPrice !== null) {
        filtered = filtered.filter((item) => item.price >= this.minPrice);
      }
      if (this.maxPrice !== null) {
        filtered = filtered.filter((item) => item.price <= this.maxPrice);
      }

      if (this.selectedTags.length > 0) {
        filtered = filtered.filter((item) =>
          this.selectedTags.some((tag) =>
            item.tags.some((productTag) => productTag.name === tag)
          )
        );
      }
      return filtered;
    },
    columns() {
      return [
        {
          title: this.$t("product.image"),
          dataIndex: "productImage",
          key: "productImage",
          scopedSlots: { customRender: "productImage" },
        },
        {
          title: this.$t("product.name"),
          dataIndex: "name",
          key: "name",
          sorter: (a, b) => a.name.localeCompare(b.name),
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "name",
          },
          sortDirections: ["descend", "ascend"],
          onFilter: (value, record) =>
            record.name.toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("product.availability"),
          dataIndex: "availability",
          key: "availability",
          scopedSlots: { customRender: "availability" },
          filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }) => (
            <div style="padding: 8px;">
              <a-checkbox-group
                value={selectedKeys}
                onChange={(checkedValues) => setSelectedKeys(checkedValues)}
                style="display: block;"
              >
                <a-checkbox value="available" style="display: block;">
                  {this.$t("product.available")}
                </a-checkbox>
                <a-checkbox value="soon" style="display: block;">
                  {this.$t("product.comingSoon")}
                </a-checkbox>
                <a-checkbox value="out" style="display: block;">
                  {this.$t("product.outOfStock")}
                </a-checkbox>
              </a-checkbox-group>
              <a-button
                type="primary"
                icon="search"
                onClick={() => confirm()}
                size="small"
                style="width: 90px; margin-top: 8px; margin-right: 8px;"
              >
                {this.$t("paiement.chercher")}
              </a-button>
              <a-button
                onClick={() => clearFilters()}
                size="small"
                style="width: 90px; margin-top: 8px;"
              >
                {this.$t("action.reinitialiser")}
              </a-button>
            </div>
          ),
          onFilter: (value, record) => value.includes(record.availability),
        },
        {
          title: this.$t("product.price"),
          dataIndex: "price",
          key: "price",
          sorter: (a, b) => a.price - b.price,
          sortDirections: ["descend", "ascend"],
        },
        {
          title: this.$t("product.quantity"),
          dataIndex: "quantity",
          key: "quantity",
          sorter: (a, b) => a.quantity - b.quantity,
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "quantity",
          },
          sortDirections: ["descend", "ascend"],
          onFilter: (value, record) =>
            record.quantity.toString().includes(value.toString()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("command.createdAt"),
          dataIndex: "createdAt",
          key: "createdAt",
          defaultSortOrder: "descend",
          sorter: (a, b) =>
            moment(a.createdAt).unix() - moment(b.createdAt).unix(),
          customRender: (text) => {
            return text
              ? moment(text).format("DD/MM/YYYY HH:mm")
              : this.$t("product.noUpdate");
          },
        },
        {
          title: this.$t("product.lastUpdate"),
          dataIndex: "updatedAt",
          key: "updatedAt",
          sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
          sortDirections: ["descend", "ascend"],
          customRender: (text) => {
            return text
              ? moment(text).format("DD/MM/YYYY HH:mm")
              : this.$t("product.noUpdate");
          },
        },
        {
          title: this.$t("product.tags"),
          dataIndex: "tags",
          key: "tags",
          scopedSlots: { customRender: "tags" },
        },
        {
          title: this.$t("action.actions"),
          dataIndex: "operation",
          scopedSlots: { customRender: "operation" },
          fixed: "right",
        },
      ];
    },
  },
  methods: {
    fetchProducts() {
      this.loading = true;
      apiClient
        .post("/marketplace/product/filter", { query: {} })
        .then((response) => {
          this.data = response.data;
          this.filterProducts();
        })
        .finally(() => {
          this.loading = false;
        })
        .catch((error) => {
          console.error("Failed to fetch products:", error);
        });
    },
    openModal(data) {
      this.mode = data ? "edit" : "add";
      this.currentProduct = data || null;
      this.showModal = true;
    },
    handleCancel() {
      this.mode = "add";
      this.currentProduct = null;
      this.showModal = false;
    },
    onSearch(event) {
      this.searchTerm = event.target.value.trim();
    },
    onPriceFilterChange() {
      if (this.minPrice === "" || this.minPrice === null) {
        this.minPrice = null;
      }
      if (this.maxPrice === "" || this.maxPrice === null) {
        this.maxPrice = null;
      }

      this.filteredData;
    },
    toggleArchived() {
      this.showArchived = !this.showArchived;
      this.filterProducts();
    },
    onTagsFilterChange(selectedTags) {
      this.selectedTags = selectedTags;
    },
    handleProductSubmit(productData) {
      this.data.push(productData);
      this.showModal = false;
      this.fetchProducts();
    },
    handleProductUpdated() {
      this.fetchProducts();
      this.showModal = false;
    },
    getTagText(availability) {
      switch (availability) {
        case "available":
          return this.$t("product.available");
        case "soon":
          return this.$t("product.comingSoon");
        case "out":
          return this.$t("product.outOfStock");
        default:
          return this.$t("product.unavailable");
      }
    },
    handleSearchTitle(e) {
      this.searchTerm = e.target.value;
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
      this.searchedColumn = "";
    },
    getTagColor(availability) {
      switch (availability) {
        case "available":
          return "green";
        case "soon":
          return "orange";
        case "out":
          return "red";
        default:
          return "gray";
      }
    },
    reactivateProduct(productId) {
      apiClient
        .post(`/marketplace/product/activate`, { query: { _id: productId } })
        .then(() => {
          this.$message.success(this.$t("success.prodReactivate"));
          this.fetchProducts();
        })
        .catch((error) => {
          console.error("Failed to reactivate product:", error);
          this.$message.error(
            this.$t("error.prodReactivate") +
              " " +
              (error.response.data.error || "")
          );
        });
    },
    supp(productId) {
      apiClient
        .post(`/marketplace/product/deactivate`, { query: { _id: productId } })
        .then(() => {
          this.$message.success(this.$t("success.prodSupp"));
          this.fetchProducts();
        })
        .catch((error) => {
          console.error("Failed to deactivate product:", error);
          this.$message.error(
            this.$t("error.autorisationSupp") +
              " " +
              (error.response.data.error || "")
          );
        });
    },
  },

  created() {
    this.fetchProducts();
  },
};
</script>
<style scoped>
.button-spacing {
  margin-right: 30px;
}
img {
  width: 50px;
  height: 50px;
}

img[src=""],
img:not([src]),
img[src*="404"] {
  display: none;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
}

.more-tag {
  cursor: pointer;
}
</style>
